import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import Favorites from "./ProfileFavorites";
import ProfileOrders from "./ProfileOrders";
import ProfileTelegramAuth from "./ProfileTelegramAuth";


export default function ProfilePage() {
  const navigate = useNavigate();

  // Основные данные пользователя
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [patronymic, setPatronymic] = useState("");
  const [login, setlogin] = useState("");
  const [email, setEmail] = useState("");
  const [showEditor, setShowEditor] = useState(false);

  // Для обновления данных
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newPatronymic, setNewPatronymic] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Для вкладок
  const [activeTab, setActiveTab] = useState("profile");
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Для Telegram mini-app
  const [telegramId, setTelegramId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertColor, setAlertColor] = useState("green");
  const isTelegramMiniApp = !!(window.Telegram && window.Telegram.WebApp);

  // Загрузка профиля пользователя
  const fetchProfile = async () => {
    try {
      const { data, status } = await authFetch("/api/accounts/profile/");
      if (status === 401) {
        navigate("/"); // или показать сообщение
        return;
      }
      setEmail(data.email || "Не указан");
      setFirstName(data.first_name || "");
      setLastName(data.last_name || "");
      setPatronymic(data.patronymic || "");
      setTelegramId(data.telegram_id || null);
    } catch {
      setEmail("Не указан");
    }
  };


  useEffect(() => {
    const storedlogin = localStorage.getItem("login");
    const token = localStorage.getItem("access_token");
    if (!token || !storedlogin) {
      navigate("/");
    } else {
      setlogin(storedlogin);
      fetchProfile();
    }
  }, [navigate]);

  // Обновление данных пользователя
  const handleUpdate = async () => {
    setMessage("");
    setError("");
    const updateData = {
      first_name: newFirstName || firstName,
      last_name: newLastName || lastName,
      patronymic: newPatronymic || patronymic,
      email: newEmail || email,
    };
    if (newPassword || confirmPassword) {
      if (newPassword !== confirmPassword) {
        setError("Пароли не совпадают");
        return;
      }
      updateData.password = newPassword;
    }

    try {
      const { data, status } = await authFetch("/api/accounts/profile/update/", {
        method: "POST",
        body: JSON.stringify(updateData),
      });
      if (status === 401) {
        setError("Для изменения данных необходимо авторизоваться");
        return;
      }
      if (status !== 200) {
        setError(data?.detail || "Не удалось обновить профиль");
        return;
      }
      setMessage("Данные успешно обновлены. Письмо отправлено на ваш email.");
      setShowEditor(false);
      setNewEmail("");
      setNewPassword("");
      setConfirmPassword("");
      setNewFirstName("");
      setNewLastName("");
      setNewPatronymic("");
      fetchProfile();
    } catch (err) {
      setError("Ошибка при обновлении профиля");
    }
  };

  // Выйти из аккаунта
  const logout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-white shadow p-6 border-b md:border-b-0 md:border-r">
        <h2 className="text-xl font-semibold mb-4 text-center md:text-left">
          Разделы
        </h2>
        <ul className="flex md:block justify-around text-sm md:space-y-2 space-x-4 md:space-x-0">
          <li>
            <button
              onClick={() => setActiveTab("profile")}
              className={`${
                activeTab === "profile"
                  ? "text-orange-600 font-bold"
                  : "text-gray-700"
              }`}
            >
              Личные данные
            </button>
          </li>
          <li>
            <button
              onClick={() => setActiveTab("orders")}
              className={`${
                activeTab === "orders"
                  ? "text-orange-600 font-bold"
                  : "text-gray-700"
              }`}
            >
              Мои заказы
            </button>
          </li>
          <li>
            <button
              onClick={() => setActiveTab("favorites")}
              className={`${
                activeTab === "favorites"
                  ? "text-orange-600 font-bold"
                  : "text-gray-700"
              }`}
            >
              Избранное
            </button>
          </li>
        </ul>
      </aside>

      <main className="flex-1 p-6">
        {activeTab === "profile" && (
          <div className="max-w-xl mx-auto bg-white rounded-xl shadow-md p-6 space-y-4">
            <h1 className="text-2xl font-bold text-orange-600">
              Личные данные
            </h1>
            <div>
              <label className="block text-sm text-gray-600">Фамилия</label>
              <div className="mt-1 p-2 bg-gray-100 rounded text-gray-800">
                {lastName}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600">Имя</label>
              <div className="mt-1 p-2 bg-gray-100 rounded text-gray-800">
                {firstName}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600">Отчество</label>
              <div className="mt-1 p-2 bg-gray-100 rounded text-gray-800">
                {patronymic}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600">Никнейм</label>
              <div className="mt-1 p-2 bg-gray-100 rounded text-gray-800">
                {login}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600">Email</label>
              <div className="mt-1 p-2 bg-gray-100 rounded text-gray-800">
                {email}
              </div>
            </div>

           {/* Telegram */}
           <div className="mt-2 p-4 border rounded-xl from-amber-100 via-orange-50 to-sky-100">
             <div className="flex items-center justify-between">
               <div className="">
                 <div className="text-sm text-gray-500">Telegram</div>
                 <div className="text-gray-800 font-semibold from-amber-100 via-orange-50 to-sky-100">
                   {telegramId ? `Привязан (ID: ${telegramId})` : "Не привязан"}
                 </div>
               </div>
               <div className="flex items-center gap-2">
                 <a href="https://t.me/mi64bot" target="_blank" rel="noreferrer"
                    className="px-3 py-2 border rounded text-sm hover:bg-gray-100">
                   Открыть бота
                 </a>
                 {telegramId && (
                   <button
                     onClick={async () => {
                       const { status } = await authFetch("/api/accounts/unbind-telegram/", { method: "POST" });
                       if (status === 200) fetchProfile();
                     }}
                     className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                   >
                     Отвязать
                   </button>
                 )}
               </div>
             </div>
             {!telegramId && <ProfileTelegramAuth onBind={fetchProfile} />}
           </div>

            <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
              <button
                onClick={() => setShowEditor(!showEditor)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm"
              >
                {showEditor ? "Скрыть" : "Изменить данные"}
              </button>

              <button
                onClick={logout}
                className="px-4 py-2 border border-gray-400 text-gray-700 rounded hover:bg-gray-100 text-sm"
              >
                Выйти
              </button>

            </div>
            {showEditor && (
              <div className="mt-6 border-t pt-6 space-y-4 animate-fade-in">
                <h2 className="text-lg font-semibold text-gray-700">
                  Редактирование
                </h2>
                <input
                  type="text"
                  placeholder="Новая фамилия"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <input
                  type="text"
                  placeholder="Новое имя"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <input
                  type="text"
                  placeholder="Новое отчество"
                  value={newPatronymic}
                  onChange={(e) => setNewPatronymic(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <input
                  type="email"
                  placeholder="Новый email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <input
                  type="password"
                  placeholder="Новый пароль"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <input
                  type="password"
                  placeholder="Повторите пароль"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <button
                  onClick={handleUpdate}
                  className="w-full py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded"
                >
                  Сохранить изменения
                </button>
                {message && <p className="text-green-600 text-sm">{message}</p>}
                {error && <p className="text-red-500 text-sm">{error}</p>}
              </div>
            )}
          </div>
        )}

        {activeTab === "favorites" && <Favorites />}

        {activeTab === "orders" && (
          <ProfileOrders active={activeTab === "orders"} />
        )}

      </main>
    </div>
  );
}
